import { useL10n } from '@uplift/hooks';
import { useCptCodesByIdsQuery, useProviderCptRates, useProviderLicenseStatesQuery, useStatesQuery } from '@uplift/queries';
import { FlexTable, FlexTableColumn, MenuItem, Modal, ModalContent, ModalTitle, MoneyCents, ScrollableView, SelectRaw, TextInputRaw } from '@uplift/ui-v2';
import { useMemo, useState } from 'react';
import { Box, InputAdornment, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Search } from '@mui/icons-material';
import { ProviderCptRateMapped } from './types';
import { State } from '@uplift/types';


export type CptRatesModalProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
};

const useStyles = makeStyles()(theme => ({
  cell: {
    width: 'auto',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    hyphens: 'none',
  },
  editAndDeleteCell: {
    padding: theme.spacing(0, 1),
    width: 'auto',
    whiteSpace: 'normal',
    overflowWrap: 'normal',
    hyphens: 'none',
  },
}));

export const CptRatesModal: React.VFC<CptRatesModalProps> = ({
  open,
  onClose,
  providerId,
}) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ selectedState, setSelectedState ] = useState<State | null>(null);
  const [ search, setSearch ] = useState('');
  const { data: states = [], isLoading: statesLoading } = useStatesQuery();
  const { data: licenseStateIds = [], isLoading: licenseStatesLoading } = useProviderLicenseStatesQuery(providerId);
  const { data: rates = [], isLoading: ratesLoading } = useProviderCptRates(providerId);
  const { data: cptCodes = [], isLoading: cptLoading } = useCptCodesByIdsQuery(rates?.map(rate => rate.service.cptCodeId));

  const isLoading = statesLoading || licenseStatesLoading || ratesLoading || cptLoading;

  const mappedRates = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return rates?.map((rate) => {
      const state = states.find(state => state.id === rate.service.stateId);
      const cpt = cptCodes?.find(cpt => cpt.id === rate.service.cptCodeId);

      if (state && cpt && licenseStateIds.includes(state.id)) {
        return {
          state: state,
          code: cpt.code,
          description: cpt.name,
          amountCents: Number(rate.priceCents),
        } satisfies ProviderCptRateMapped;
      }
    }).filter(rate => rate !== undefined);
  }, [ isLoading, rates, states, cptCodes, licenseStateIds ]);

  const filterStates = useMemo(() => {
    const statesForFilter = states.filter(state => licenseStateIds.includes(state.id)).sort((a, b) => a.fullName.localeCompare(b.fullName));

    if (selectedState === null && statesForFilter[0]) {
      setSelectedState(statesForFilter[0]);
    }

    return statesForFilter;
  }, [ licenseStateIds, selectedState, states ]);

  const filteredRates = useMemo(() => {
    return mappedRates?.filter((rate) => {
      if (selectedState?.id || search.length > 0) {
        const matchesState = rate?.state?.id === selectedState?.id;

        const matchesSearch = rate?.code?.toLowerCase().includes(search) ||
          rate?.description?.toLowerCase().includes(search);

        return matchesState && matchesSearch;
      } else {
        return rate;
      }
    });
  }, [ mappedRates, search, selectedState ]);

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: 'CPT Code',
      sortable: true,
      sortResettable: true,
      cellClassName: classes.cell,
      renderCell: (row) => (
        <Typography variant="body2" color="textPrimary">
          {row.row.code}
        </Typography>
      ),
    } as FlexTableColumn<ProviderCptRateMapped>,
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      sortResettable: true,
      cellClassName: classes.cell,
      renderCell: (row) => (
        <Typography variant="body2" color="textPrimary">
          {row.row.description}
        </Typography>
      ),
    } as FlexTableColumn<ProviderCptRateMapped>,
    {
      field: 'amountCents',
      headerName: 'Amount',
      sortable: true,
      sortResettable: true,
      cellClassName: classes.cell,
      renderCell: (row) => (
        <Typography variant="body2" color="textPrimary">
          <MoneyCents value={row.row.amountCents} />
        </Typography>
      ),
    } as FlexTableColumn<ProviderCptRateMapped>,
  ], [ classes ]);

  return (
    <Modal
      open={open}
      onClose={() => {
        setSearch('');
        setSelectedState(null);
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <ModalTitle onClose={onClose}>
        {`${l10n.providerCptRates.title} - ${selectedState?.fullName}`}
      </ModalTitle>
      <ModalContent>
        <Box display="flex" flexDirection="row" gap={3} flex={1} mb={2}>
          <SelectRaw
            fullWidth
            name="state"
            color="secondary"
            value={selectedState?.id ?? ''}
            defaultValue={selectedState?.id ?? ''}
            onChange={(e) => setSelectedState(filterStates.find(state => state.id === e.target.value) ?? null)}
            displayEmpty
          >
            {!isLoading && filterStates?.map((state) => (
              <MenuItem key={state.id} value={state.id}>{state.fullName}</MenuItem>
            ))}
          </SelectRaw>
          <TextInputRaw
            fullWidth
            name="cptSearch"
            placeholder={l10n.providerCptRates.search}
            onChange={(e) => setSearch(e.target.value.trim().toLowerCase())}
            value={search}
            setValue={setSearch}
            startAdornment={(
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            )}
            autoFocus
            autoComplete="off"
            color="secondary"
          />
        </Box>
        <Box height="100vh">
          <ScrollableView width="100%" height="100%">
            <FlexTable
              rows={filteredRates}
              columns={columns}
              initialSort={{ colId: 'code', type: 'asc' }}
              infiniteScroll={{ perPage: 100 }}
              isLoading={isLoading}
              horizontalScroll={true}
            />
          </ScrollableView>
        </Box>
      </ModalContent>
    </Modal>
  );
};
